/**
 * =============================================================================
 * ALERT DETAIL PAGE
 * =============================================================================
 * 
 * Full page view for a single alert with 3-pane layout:
 * - Center: Alert card (pinned) + Activity logs
 * - Right: AI Chat (resizable)
 * 
 * NO bottom chat box - that's only on the home page.
 * 
 * Route: /alert/:alertId
 */

import { Box, Loader, Text, Stack } from '@mantine/core';
import { Warning, ArrowLeft } from '@phosphor-icons/react';
import AppLayout from '@layouts/AppLayout';
import { useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useState } from 'react';

import { AlertDetailLayout } from '@components/TodoChat';
import { fetchAlertById } from '@components/TodoChat/api';
import type { AlertItem } from '@components/TodoChat';

const AlertDetailPage: React.FC = () => {
    const { alertId } = useParams<{ alertId: string }>();
    const navigate = useNavigate();
    
    const [alert, setAlert] = useState<AlertItem | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load alert data
    useEffect(() => {
        const loadAlert = async () => {
            if (!alertId) {
                setError('No alert ID provided');
                setIsLoading(false);
                return;
            }
            
            setIsLoading(true);
            setError(null);
            
            try {
                const alertData = await fetchAlertById(alertId);
                if (alertData) {
                    setAlert(alertData);
                } else {
                    setError('Alert not found');
                }
            } catch (err) {
                console.error('Error loading alert:', err);
                setError('Failed to load alert. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        
        loadAlert();
    }, [alertId]);

    // Handle back navigation
    const handleBack = useCallback(() => {
        navigate('/home');
    }, [navigate]);

    // Handle alert updates
    const handleAlertUpdated = useCallback((id: string, action: 'deleted' | 'completed' | 'snoozed' | 'ignored') => {
        console.log(`[AlertDetail] Alert ${id} ${action}`);
        // Navigate back to home after action
        if (action === 'deleted' || action === 'completed' || action === 'ignored') {
            navigate('/home');
        }
    }, [navigate]);

    // Loading state
    if (isLoading) {
        return (
            <AppLayout>
                <Box className="flex h-screen w-full items-center justify-center">
                    <Loader size="lg" color="blue" />
                </Box>
            </AppLayout>
        );
    }

    // Error state
    if (error || !alert) {
        return (
            <AppLayout>
                <Box className="flex h-screen w-full flex-col items-center justify-center">
                    <Warning size={48} weight="light" className="text-halt-800 mb-4" />
                    <Text className="text-lg font-semibold text-gray-800 mb-2">
                        {error || 'Alert not found'}
                    </Text>
                    <Text 
                        className="text-sm text-blu-800 cursor-pointer hover:underline"
                        onClick={handleBack}
                    >
                        ← Back to Home
                    </Text>
                </Box>
            </AppLayout>
        );
    }

    // Main render - 3-pane layout WITHOUT bottom chat
    return (
        <AppLayout>
            <Box className="h-screen w-full overflow-hidden">
                <AlertDetailLayout
                    alert={alert}
                    onBack={handleBack}
                    onAlertUpdated={handleAlertUpdated}
                    currentUser={{ name: 'You' }}
                />
            </Box>
        </AppLayout>
    );
};

export default AlertDetailPage;
